// Master fix plan — ONE Glean call triaging every cluster of the selected run into
// an ordered, leverage-first fix plan (instructions: config/glean-master-prompt.md).
//
// Manual start: the Generate button is the only spend (cached per job+run — one
// spend, then free). Collapsed by default; the whole header is the toggle. The run
// belongs to the server, so it survives closing/navigation — on mount we probe
// (free) and render a cached plan or resume watching an in-flight generation.
import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export default function MasterFixPlan({ jobId, runId, clusterCount }) {
  const [state, setState] = useState('idle'); // idle | loading | done | error
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);

  const base = `/api/jobs/${encodeURIComponent(jobId)}/master-analysis${runId ? `?run=${encodeURIComponent(runId)}` : ''}`;

  // reveal: a user click opens the result when it lands; the silent mount-time
  // cache read leaves the card collapsed.
  async function generate(force = false, { reveal = false } = {}) {
    setState('loading'); setError(null);
    try {
      const res = await fetch(`${base}${force ? (runId ? '&' : '?') + 'force=1' : ''}`, { method: 'POST' });
      // Safe parse: a proxy/gateway error answers with HTML, and a blind .json()
      // would show the user "Unexpected token '<'" instead of the real problem.
      const out = await res.json().catch(() => null);
      if (!res.ok) { const e = new Error(out?.error || `the API answered HTTP ${res.status}`); e.status = res.status; throw e; }
      if (!out) throw new Error('the API returned a non-JSON response — is the API server up?');
      setResult(out); setState('done');
      if (reveal) setOpen(true);
    } catch (e) { setError(e); setState('error'); }
  }

  // Probe on mount / run change: cached → render instantly (free); running →
  // resume watching. A fresh run stays idle — the Generate button is the spend.
  useEffect(() => {
    let live = true; let timer = null;
    setState('idle'); setResult(null); setError(null); setOpen(false);
    let wasWatching = false;
    const watch = async () => {
      try {
        const p = await (await fetch(base)).json();
        if (!live) return;
        if (p.cached) { await generate(false); return; } // cache hit — instant, no spend
        if (p.running) { wasWatching = true; setState('loading'); timer = setTimeout(watch, 3000); return; }
        if (wasWatching) { setError({ message: 'the background generation did not complete — re-run it' }); setState('error'); }
      } catch { /* probe is best-effort */ }
    };
    watch();
    return () => { live = false; if (timer) clearTimeout(timer); };
  }, [jobId, runId]);

  if (!clusterCount) return null;

  const toggleable = state === 'done' && result;

  return (
    <section className="card card--flat">
      <header
        className={`card__head${toggleable ? ' card__head--toggle' : ''}`}
        onClick={toggleable ? () => setOpen((v) => !v) : undefined}
        role={toggleable ? 'button' : undefined}
        aria-expanded={toggleable ? open : undefined}
        tabIndex={toggleable ? 0 : undefined}
        onKeyDown={toggleable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((v) => !v); } } : undefined}
      >
        <div>
          <h2 className="card__title">
            {toggleable && <span className="chev" aria-hidden="true">{open ? '▾' : '▸'}</span>}
            Master fix plan
          </h2>
          <p className="card__subtitle">
            {toggleable && !open
              ? `Ready — one Glean triage across all ${clusterCount} signals · click to expand`
              : `One Glean triage across all ${clusterCount} signals in this run — shared root causes, fixes ordered by tests unblocked`}
          </p>
        </div>
        <span className="prompt__actions" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="primary-btn"
            disabled={state === 'loading'}
            onClick={() => generate(state === 'done', { reveal: true })}
          >
            {state === 'loading' ? 'Generating… (1–2 min)' : state === 'done' ? 'Re-generate' : '✨ Generate fix plan'}
          </button>
        </span>
      </header>

      {state === 'loading' && (
        <p className="ai__note"><span className="ai-spin" aria-hidden="true" /> Glean is triaging all {clusterCount} signals — reading evidence, our code and runbooks…</p>
      )}
      {state === 'error' && (
        <p className={`ai__note ${error?.status === 503 ? '' : 'ai__note--error'}`}>
          {error?.status === 503
            ? 'AI analysis is not configured — set GLEAN_TOKEN on the API (Glean admin → Client API Tokens).'
            : `Generation failed: ${error?.message}`}
        </p>
      )}
      {state === 'done' && result && open && (
        <div className="ai">
          <div className="ai__md">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{result.analysis}</ReactMarkdown>
          </div>
          <p className="ai__meta">
            Generated by Glean{result.cached ? ' (cached)' : ''} across {result.clusters} signals
            {' '}· verify before acting — per-cluster evidence and PR prompts stay the human-in-the-loop path
          </p>
        </div>
      )}
    </section>
  );
}
